export const VPC_QUALIFYING_TERMS = ['', 'primary care', 'internal medicine'];

export const VPC_QUALIFYING_SPECIALTIES = ['Primary Care', 'Internal Medicine'];

export const VPC_QUALIFYING_TREATMENTS = ['Virtual Care'];

// Subset of VPC_QUALIFYING_TERMS without '' — the zero-state gate is handled separately in
// isVpcBannerQualifyingSearch; this constant only matches non-blank terms.
export const VPC_BANNER_QUALIFYING_TERMS = VPC_QUALIFYING_TERMS.filter((t) => t !== '');

// ---------------------------------------------------------------------------
// VPC Banner display gate (AHSMKTG-4137)
// ---------------------------------------------------------------------------

/**
 * Returns true when the current search qualifies the VPC Banner to appear.
 *
 * Qualifying conditions:
 *   1. qualifying specialty selected (Primary Care / Internal Medicine) — OR-independent of term
 *   2. non-blank qualifying term ('primary care' / 'internal medicine') — OR-independent of facets
 *   3. blank-term gate (normalizedTerm === ''):
 *      a. zero-state: no specialties, no treatments, no other facet selections
 *      b. qualifying treatment selected ('Virtual Care')
 *
 * Conditions 1 and 2 are OR-independent. Condition 3 applies only when the search term is blank;
 * a non-blank non-qualifying term with a qualifying treatment does not qualify.
 *
 * @param {object}   opts
 * @param {string}   opts.term                    - Current Coveo query term
 * @param {string[]} opts.specialties             - Selected specialty facet values (title-case)
 * @param {string[]} [opts.treatments=[]]         - Selected treatment facet values
 * @param {boolean}  [opts.hasOtherFacetSelections=false] - True when any facet that is not
 *                                                 individually checked (type, condition, accepted
 *                                                 insurance) has at least one selected value
 * @returns {boolean}
 *
 * NOTE: The FAD counterpart in deriveScenarioKey.js additionally accepts `specialtyServices` and
 * `isVpcChecked` for FAD-only facets (Virtual Care specialtyservice, VPC checkbox) not present in
 * LocationSearchContext.js. Extend this signature if those facets are added to LocationSearch.
 */
export function isVpcBannerQualifyingSearch({
    term,
    specialties,
    treatments = [],
    hasOtherFacetSelections = false,
}) {
    const sa = specialties ?? [];
    const ta = treatments ?? [];

    // Qualifying specialty — OR-independent; applies regardless of term.
    if (sa.some((s) => VPC_QUALIFYING_SPECIALTIES.includes(s))) return true;

    const normalizedTerm = (term ?? '').toLowerCase().trim();

    // Non-blank qualifying terms qualify on their own, regardless of facets.
    if (VPC_BANNER_QUALIFYING_TERMS.includes(normalizedTerm)) return true;

    // Blank-term gate: show banner only when the search is VPC-relevant with no term typed.
    if (normalizedTerm === '') {
        // Zero-state: no facets selected at all → show.
        if (!sa.length && !ta.length && !hasOtherFacetSelections) return true;

        // 'Virtual Care' treatment with blank term → show. Applies only to blank-term searches;
        // a non-blank non-qualifying term with Virtual Care selected does not qualify.
        if (ta.some((t) => VPC_QUALIFYING_TREATMENTS.includes(t))) return true;
    }

    return false;
}
